import React, { Component } from 'react';
import moment from 'moment';
import ProductSalesAPI from '../../../services/productSalesService';

// Product Sales report (History → "Product Sales" tab). Two views:
//   Standalone — pure product sales (no job)         → GET /api/product-sales
//   Upsold     — products sold at time of service    → GET /api/product-sales/upsold
// Self-contained: own sub-toggle, date range, pagination.

const PAGE_SIZE = 50;
const money = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? moment(d).format('MMM D, YYYY') : '—');

class ProductSalesReport extends Component {
  state = {
    view: 'standalone',          // 'standalone' | 'upsold'
    rows: [],
    total: 0,
    page: 1,
    loading: false,
    error: null,
    startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  };

  componentDidMount() { this.load(); }

  load = async () => {
    const { view, page, startDate, endDate } = this.state;
    const { installerId, locationId } = this.props;
    this.setState({ loading: true, error: null });
    try {
      const params = {
        locationId: locationId && locationId !== 'all' ? locationId : undefined,
        dateFrom: startDate,
        dateTo: endDate,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      let rows = [];
      let total = 0;
      if (view === 'standalone') {
        const res = await ProductSalesAPI.getSales(installerId, { status: 'completed', ...params });
        rows = res?.sales || res?.data || [];
        total = res?.pagination?.total ?? rows.length;
      } else {
        const res = await ProductSalesAPI.getUpsold(installerId, params);
        rows = res?.data || [];
        total = res?.pagination?.total ?? rows.length;
      }
      this.setState({ rows, total, loading: false });
    } catch (err) {
      this.setState({ loading: false, error: 'Could not load product sales.', rows: [], total: 0 });
    }
  };

  setView = (view) => this.setState({ view, page: 1, rows: [] }, this.load);
  setDate = (field, value) => this.setState({ [field]: value, page: 1 }, this.load);
  setPage = (page) => this.setState({ page }, this.load);

  render() {
    const { view, rows, total, page, loading, error, startDate, endDate } = this.state;
    const TEAL = '#3FB2BE';
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const tab = (key, label) => (
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => this.setView(key)}
        style={{
          borderRadius: 9999, fontWeight: view === key ? 600 : 500, fontSize: 13,
          background: view === key ? TEAL : '#fff',
          color: view === key ? '#fff' : '#6B7280',
          border: view === key ? 'none' : '1px solid #E5E7EB',
        }}
      >{label}</button>
    );

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12, marginBottom: 16 }}>
          <div className="d-flex" style={{ gap: 8 }}>
            {tab('standalone', 'Standalone')}
            {tab('upsold', 'Upsold at service')}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={startDate} onChange={(e) => this.setDate('startDate', e.target.value)} />
            <span className="text-muted">–</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={endDate} onChange={(e) => this.setDate('endDate', e.target.value)} />
          </div>
        </div>

        {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
        {loading && <div className="text-muted text-center py-4">Loading…</div>}

        {!loading && !error && rows.length === 0 && (
          <div className="text-muted text-center py-4" style={{ fontSize: 14 }}>
            No {view === 'upsold' ? 'upsold' : 'standalone'} product sales in this range.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <table className="jv-table jv-table-sm" style={{ width: '100%' }}>
            {view === 'standalone' ? (
              <>
                <thead><tr><th>Date</th><th>Sale #</th><th>Buyer</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{fmtDate(r.created_at)}</td>
                      <td>🛒 {r.sale_number || '—'}</td>
                      <td>{r.buyer_display || r.customer_name || 'Walk-in'}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                <thead><tr><th>Date</th><th>Product</th><th>Customer</th><th>Vehicle</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.request_service_id || `${r.request_id}-${r.product_id}`}>
                      <td>{fmtDate(r.date)}</td>
                      {/* `custom_description` is the LINE's own text, captured at
                          sale time — it survives the product being renamed or
                          deleted afterwards, which `product_name` does not. */}
                      <td>
                        {r.product_name
                          ? (r.brand ? `${r.brand} - ${r.product_name}` : r.product_name)
                          : (r.custom_description || '—')}
                      </td>
                      <td>{r.customer_name || '—'}</td>
                      <td>{r.vehicle || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        )}

        {!loading && total > PAGE_SIZE && (
          <div className="d-flex align-items-center justify-content-center" style={{ gap: 12, marginTop: 12 }}>
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => this.setPage(page - 1)}>Prev</button>
            <span className="text-muted" style={{ fontSize: 13 }}>Page {page} of {pages}</span>
            <button className="btn btn-sm btn-outline-secondary" disabled={page >= pages} onClick={() => this.setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
    );
  }
}

export default ProductSalesReport;
